import React from 'react'
import { SelectCard } from 'src/Components/Ui/SelectCard'
import { TabPanel } from 'src/Components/Ui/TabPanel'
import { State, PartialAction, Position } from 'src/types'
import { useDispatcher } from 'src/features/atoms/glove/us/logic/dispatchGenerator'
import { useOriginDispatcher } from 'src/hooks/useOriginDispatcher'
import { useLocale } from 'src/locales/useLocale'
import {
  colorCautionGenerator,
  hamidashiObjectSwitcher,
  laceObjectsSwitcher,
  getPartsItems,
  useSelectPartsObject
} from 'src/features/atoms/glove/us/logic/colorSetting'
import { SelectCardWithImage } from 'src/Components/Ui/SelectCardWithImage'
import { DISPATCHER } from 'src/features/atoms/glove/us/constants/actionType'

type Props = {
  state: State
  selectedIndex: number
  figurePanelNum: number
  position: Position
  dispatch: React.Dispatch<PartialAction>
}

export const ColorSetter: React.FC<Props> = ({ state, selectedIndex, figurePanelNum, position, dispatch }) => {
  const { tanner, parts, atomsLabel, lace, stitch, hamidashi, mouton } = state
  const partsKey = parts?.value
  const { t } = useLocale()
  const partsObjects = useSelectPartsObject(figurePanelNum, position, state, t)
  const handle = {
    [partsKey]: useOriginDispatcher(DISPATCHER, partsKey, dispatch, t.DISPATCH_US_LEATHER_COLOR_OBJECTS[partsKey]),
    parts: useOriginDispatcher(DISPATCHER, 'parts', dispatch, partsObjects),
    lace: useDispatcher('lace', dispatch, t),
    stitch: useDispatcher('stitch', dispatch, t),
    hamidashi: useDispatcher('hamidashi', dispatch, t),
    mouton: useDispatcher('mouton', dispatch, t),
    atomsLabel: useDispatcher('atomsLabel', dispatch, t)
  }
  const colorParts = getPartsItems(position, state)

  if (!colorParts || tanner?.value === 'unselected') return null
  const partsLabel = parts?.label
  return (
    <TabPanel {...{ selectedIndex }} index={1}>
      <SelectCard summary={t.PARTS} selectedLabel={partsLabel} objects={partsObjects} handleChange={handle.parts} />
      <SelectCard
        summary={`${partsLabel}${t.COLOR}`}
        selectedLabel={colorParts[partsKey]?.label}
        selectedColor={colorParts[partsKey]?.color}
        objects={t.DISPATCH_US_LEATHER_COLOR_OBJECTS[partsKey]}
        handleChange={handle[partsKey]}
        isError={colorParts[partsKey]?.value === 'unselected'}
      />
      <SelectCard
        summary={t.LACE}
        selectedLabel={lace.label}
        selectedColor={lace.color}
        objects={laceObjectsSwitcher(position, t)}
        defaultExpanded={false}
        caution={colorCautionGenerator('lace', t)}
        handleChange={handle.lace}
        isError={lace?.value === 'unselected'}
      />
      <SelectCard
        summary={t.STITCH}
        selectedLabel={stitch.label}
        selectedColor={stitch.color}
        objects={t.STITCH_COLOR_OBJECTS}
        defaultExpanded={false}
        handleChange={handle.stitch}
        isError={stitch?.value === 'unselected'}
      />
      <SelectCard
        summary={t.HAMIDASHI}
        selectedLabel={hamidashi.label}
        selectedColor={hamidashi.color}
        objects={hamidashiObjectSwitcher(position, t)}
        defaultExpanded={false}
        handleChange={handle.hamidashi}
        isError={hamidashi?.value === 'unselected'}
      />
      <SelectCard
        summary={t.MOUTON}
        selectedLabel={mouton.label}
        selectedColor={mouton.color}
        objects={t.MOUTON_COLOR_OBJECTS}
        defaultExpanded={false}
        handleChange={handle.mouton}
        isError={mouton?.value === 'unselected'}
      />
      <SelectCardWithImage
        summary={t.LABEL}
        selectedLabel={atomsLabel.label}
        selectedColor={atomsLabel.color}
        objects={t.LABEL_COLOR_OBJECTS}
        imageWidth={120}
        imageHeight={90}
        handleChange={handle.atomsLabel}
        isError={atomsLabel?.value === 'unselected'}
      />
    </TabPanel>
  )
}
